import React from "react";
import { Link } from "react-router-dom";
import Hero from "@/components/Hero";
import { Marquee } from "@/components/Marquee";
import { Reveal } from "@/components/Reveal";
import { SectionHead } from "@/components/SectionHead";
import ProductCard from "@/components/ProductCard";
import { useContent } from "@/context/ContentContext";
import { SHOPIFY_STOREFRONT_URL } from "@/lib/api";
import { usePageMeta } from "@/hooks/usePageMeta";
import { ArrowRight } from "lucide-react";
import BioText from "@/components/BioText";

export default function Home() {
  usePageMeta(
    "CHILLWOAH — Okaytwitchy | Music, Culture, Community",
    "Enter the world of CHILLWOAH — independent Brooklyn artist Okaytwitchy. Stream the music, watch the films, shop XRABI and claim your Passport number."
  );
  const { products, content } = useContent();
  // Featured lineup: Just The Tip tee / CHILLWOAH trucker hat / OG One tee / Back On My Bullshit tee — exact uploaded photos, real Shopify listings.
  const featuredIds = ["chillwoah-oversized-tee", "xrabi-trucker-cap", "og-oversized-tee", "back-on-my-bullshit-tee"];
  const featured = featuredIds.map((id) => products.find((p) => p.id === id)).filter(Boolean);

  return (
    <div data-testid="home-page" className="pb-28">
      <Hero />

      <Marquee
        items={["CHILLWOAH", "MUSIC • CULTURE • COMMUNITY", "XRABI", "BROOKLYN NY", "OKAYTWITCHY", "PASSPORT"]}
      />

      {/* THE DROP — featured pieces, primary business goal */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24" aria-labelledby="xrabi-drop-heading">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-10 sm:mb-14">
          <div>
            <p className="font-mono text-xs sm:text-sm font-semibold tracking-[0.25em] uppercase text-[#F26522]">
              The Fashion — Drop 01
            </p>
            <h2 id="xrabi-drop-heading" className="mt-3 font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold uppercase tracking-tight text-white leading-[1.05]">
              XRABI / The Uniform
            </h2>
            <p className="mt-4 max-w-xl text-zinc-300 text-base sm:text-lg">
              Heavyweight fabrics, restrained palettes, cut like photographs. The most-wanted pieces of the movement —
              worn with the sound.
            </p>
          </div>
          <a
            href={SHOPIFY_STOREFRONT_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="home-shop-all-button"
            className="shrink-0 h-16 px-8 border-2 border-white/80 hover:border-[#F26522] hover:text-[#F26522] text-white font-display font-extrabold uppercase tracking-widest flex items-center justify-center gap-3 transition-colors"
          >
            Enter the full shop <ArrowRight className="w-5 h-5" aria-hidden="true" />
          </a>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
          {featured.map((p, i) => (
            <Reveal key={p.id} delay={i * 0.1} className="h-full">
              <ProductCard product={p} index={i} />
            </Reveal>
          ))}
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <Reveal className="lg:col-span-5">
            <div className="relative border border-[#1A1A22] bg-[#0F0F14]">
              <img
                src={content?.images?.bio}
                alt="Okaytwitchy as a child with his mother — vintage CHILLWOAH photograph"
                loading="lazy"
                className="w-full h-auto"
              />
              <span className="absolute bottom-4 left-4 font-mono text-xs tracking-[0.25em] uppercase text-[#F5E6D3] bg-[#08080A]/85 backdrop-blur px-3 py-1.5 border border-[#2A2A36]">
                Brooklyn, NY
              </span>
            </div>
          </Reveal>
          <div className="lg:col-span-7">
            <SectionHead eyebrow="The Artist" title="Okaytwitchy" index="/ 05" />
            <Reveal delay={0.1}>
              <BioText />
              <Link
                to="/bio"
                data-testid="home-bio-link"
                className="mt-8 inline-flex h-16 px-8 border-2 border-white/80 hover:border-[#F26522] hover:text-[#F26522] text-white font-display font-extrabold uppercase tracking-widest items-center gap-3 transition-colors"
              >
                Read the story <ArrowRight className="w-5 h-5" aria-hidden="true" />
              </Link>
            </Reveal>
          </div>
        </div>
      </section>
    </div>
  );
}
